<template>
  <div v-if="hasActions" class="cr-actions-menu-wrap">
    <v-menu location="bottom end" offset="8" :close-on-content-click="true">
      <template #activator="{ props: menuProps }">
        <button
          type="button"
          class="cr-actions-trigger"
          aria-label="Member actions"
          v-bind="menuProps"
          @click.stop
        >
          <v-icon icon="mdi-dots-vertical" size="18" />
        </button>
      </template>

      <div class="cr-actions-menu" role="menu">
        <button
          type="button"
          class="cr-actions-menu__item"
          role="menuitem"
          @click="$emit('edit', user)"
        >
          <span class="cr-actions-menu__icon cr-actions-menu__icon--edit">
            <v-icon icon="mdi-pencil-outline" size="18" />
          </span>
          <span class="cr-actions-menu__text">
            <span class="cr-actions-menu__title">Edit</span>
            <span class="cr-actions-menu__desc">Update member profile</span>
          </span>
        </button>

        <button
          v-if="canToggleStatus"
          type="button"
          class="cr-actions-menu__item"
          role="menuitem"
          @click="$emit('toggle-status', user)"
        >
          <span
            class="cr-actions-menu__icon"
            :class="isActive ? 'cr-actions-menu__icon--warning' : 'cr-actions-menu__icon--success'"
          >
            <v-icon
              :icon="isActive ? 'mdi-account-off-outline' : 'mdi-account-check-outline'"
              size="18"
            />
          </span>
          <span class="cr-actions-menu__text">
            <span class="cr-actions-menu__title">
              {{ isActive ? 'Deactivate' : 'Activate' }}
            </span>
            <span class="cr-actions-menu__desc">
              {{
                isActive
                  ? 'Pause portal access for this member'
                  : 'Restore portal access for this member'
              }}
            </span>
          </span>
        </button>

        <button
          v-if="isHuman"
          type="button"
          class="cr-actions-menu__item"
          role="menuitem"
          @click="$emit('set-extension', user)"
        >
          <span class="cr-actions-menu__icon cr-actions-menu__icon--edit">
            <v-icon icon="mdi-phone-settings-outline" size="18" />
          </span>
          <span class="cr-actions-menu__text">
            <span class="cr-actions-menu__title">Softphone extension</span>
            <span class="cr-actions-menu__desc">
              Provision SIP credentials for browser calling
            </span>
          </span>
        </button>

        <button
          type="button"
          class="cr-actions-menu__item cr-actions-menu__item--danger"
          role="menuitem"
          :disabled="isSelf || isDefaultAIAgent"
          @click="$emit('delete', user)"
        >
          <span class="cr-actions-menu__icon cr-actions-menu__icon--delete">
            <v-icon icon="mdi-trash-can-outline" size="18" />
          </span>
          <span class="cr-actions-menu__text">
            <span class="cr-actions-menu__title">Remove</span>
            <span class="cr-actions-menu__desc">
              {{
                isSelf
                  ? 'You cannot remove your own account'
                  : isDefaultAIAgent
                    ? 'The default AI agent cannot be removed'
                    : 'Delete this member from the team'
              }}
            </span>
          </span>
        </button>
      </div>
    </v-menu>
  </div>
  <span v-else class="us-row__empty">—</span>
</template>

<script setup>
import { computed } from 'vue'
import { isHumanAgent, resolveListAgentType } from '@/utils/orgUsers'

defineOptions({ name: 'UsersActionMenu' })

const props = defineProps({
  user: { type: Object, required: true },
  currentUserId: { type: String, default: '' },
})

defineEmits(['edit', 'delete', 'toggle-status', 'set-extension'])

const isOwner = computed(
  () => String(props.user.role ?? '').toUpperCase() === 'OWNER',
)

const isSelf = computed(
  () => String(props.user.id ?? '') === String(props.currentUserId ?? ''),
)

// The onboarding-provisioned AI agent -- protected the same way the org
// owner is, just without hiding the rest of the menu (its language/gender
// are still editable, and it's needed for figuring out overall status).
const isDefaultAIAgent = computed(() => Boolean(props.user.isOnboardingDefault))

const isActive = computed(
  () => String(props.user.status ?? '').toUpperCase() === 'ACTIVE',
)

// Softphone provisioning targets human agents (role AGENT + HUMAN_AGENT).
const isHuman = computed(
  () =>
    String(props.user.role ?? '').toUpperCase() === 'AGENT' &&
    isHumanAgent(resolveListAgentType(props.user)),
)

const canToggleStatus = computed(() => {
  if (isOwner.value) return false
  const status = String(props.user.status ?? '').toUpperCase()
  if (status === 'ACTIVE') return !isSelf.value
  return status === 'INACTIVE'
})

const hasActions = computed(() => !isOwner.value)
</script>
